import { useCallback, useEffect, useState } from 'react';
import type { PeriodMonth } from '../types/timesheet.types';
import type {
  ChaseNotification,
  ChaseNotificationPayload,
  ChaseNotificationFilters,
} from '../types/connector.types';
import { useConnector } from '../contexts/ConnectorContext';
import { errorLogger } from '../utils/errorLogger';

export interface UseChaseNotificationsResult {
  history: ChaseNotification[];
  isLoading: boolean;
  isSending: boolean;
  error: Error | null;
  /** Sends one chase and appends the result to history. */
  sendChase: (payload: ChaseNotificationPayload) => Promise<ChaseNotification>;
  /** Sends chases concurrently (capped). Returns per-payload results. */
  sendBulk: (payloads: ChaseNotificationPayload[]) => Promise<{
    succeeded: ChaseNotification[];
    failed: Array<{ payload: ChaseNotificationPayload; error: string }>;
  }>;
  /** Reloads the last N days of history (default: initial filter). */
  refresh: () => Promise<void>;
  /** Count of chases already sent for a given personEmail + periodMonth. */
  countFor: (personEmail: string, periodMonth: PeriodMonth) => number;
}

/**
 * Loads chase notification history (for the audit panel / throttling) and
 * exposes send helpers for individual + bulk chases.
 *
 * Pass `initialFilters` to scope the loaded history. Defaults to the last
 * 60 days so the dashboard doesn't pull the entire audit archive.
 */
export function useChaseNotifications(
  initialFilters?: ChaseNotificationFilters
): UseChaseNotificationsResult {
  const connector = useConnector();

  const [history, setHistory] = useState<ChaseNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const defaultSince = (): string => {
    const d = new Date();
    d.setDate(d.getDate() - 60);
    return d.toISOString();
  };

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const rows = await connector.getChaseNotifications({
        since: defaultSince(),
        ...initialFilters,
      });
      setHistory(rows);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      errorLogger.log(e, { component: 'useChaseNotifications', action: 'load' });
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [connector, initialFilters]);

  useEffect(() => { void load(); }, [load]);

  const sendChase = useCallback(
    async (payload: ChaseNotificationPayload): Promise<ChaseNotification> => {
      setIsSending(true);
      setError(null);
      try {
        const saved = await connector.sendChaseNotification(payload);
        setHistory(prev => [saved, ...prev]);
        return saved;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        errorLogger.log(e, {
          component: 'useChaseNotifications',
          action: 'sendChase',
          userEmail: payload.personEmail,
        });
        setError(e);
        throw e;
      } finally {
        setIsSending(false);
      }
    },
    [connector]
  );

  const sendBulk = useCallback(
    async (payloads: ChaseNotificationPayload[]) => {
      setIsSending(true);
      setError(null);
      const succeeded: ChaseNotification[] = [];
      const failed: Array<{ payload: ChaseNotificationPayload; error: string }> = [];
      try {
        // Serialise to avoid hitting the SP user cache with a thundering herd.
        for (const p of payloads) {
          try {
            const saved = await connector.sendChaseNotification(p);
            succeeded.push(saved);
          } catch (err) {
            failed.push({
              payload: p,
              error: err instanceof Error ? err.message : String(err),
            });
          }
        }
        if (succeeded.length > 0) {
          setHistory(prev => [...succeeded, ...prev]);
        }
        return { succeeded, failed };
      } finally {
        setIsSending(false);
      }
    },
    [connector]
  );

  const countFor = useCallback(
    (personEmail: string, periodMonth: PeriodMonth): number => {
      return history.filter(h => h.personEmail === personEmail && h.periodMonth === periodMonth).length;
    },
    [history]
  );

  return {
    history,
    isLoading,
    isSending,
    error,
    sendChase,
    sendBulk,
    refresh: load,
    countFor,
  };
}
